// Trust Badges Component - BWY Certification
import { motion } from "framer-motion";

// BWY Badge - Links to British Wheel of Yoga
export const BWYBadge = ({ size = "md", showText = true, className = "" }) => {
  const sizes = {
    sm: { badge: "w-12 h-12", text: "text-xs" },
    md: { badge: "w-16 h-16", text: "text-sm" },
    lg: { badge: "w-20 h-20", text: "text-base" }
  };

  const { badge, text } = sizes[size];

  return (
    <a
      href="https://bwy.org.uk/"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex flex-col items-center gap-2 group ${className}`}
      title="British Wheel of Yoga Certified Teacher"
      data-testid="bwy-badge"
    >
      <div className={`${badge} relative`}>
        {/* BWY Logo - Official wheel design */}
        <svg
          viewBox="0 0 100 100"
          className="w-full h-full transition-transform group-hover:scale-105"
          aria-label="British Wheel of Yoga Logo"
        >
          {/* Outer circle */}
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#C5A059"
            strokeWidth="2"
          />
          {/* Inner wheel spokes */}
          <g stroke="#C5A059" strokeWidth="1.5">
            <line x1="50" y1="10" x2="50" y2="90" />
            <line x1="10" y1="50" x2="90" y2="50" />
            <line x1="21.72" y1="21.72" x2="78.28" y2="78.28" />
            <line x1="78.28" y1="21.72" x2="21.72" y2="78.28" />
          </g>
          {/* Center circle */}
          <circle
            cx="50"
            cy="50"
            r="12"
            fill="#C5A059"
          />
          {/* Inner decorative circle */}
          <circle
            cx="50"
            cy="50"
            r="8"
            fill="none"
            stroke="#FAF7F2"
            strokeWidth="1"
          />
          {/* Lotus petal hints at center */}
          <path
            d="M50 42 Q53 50 50 58 Q47 50 50 42"
            fill="#FAF7F2"
          />
        </svg>
      </div>
      {showText && (
        <span className={`${text} text-neutral-600 group-hover:text-gold transition-colors text-center leading-tight`}>
          British Wheel<br />of Yoga
        </span>
      )}
    </a>
  );
};

// Trust Section for Homepage - Shows all certifications
export const TrustBadgesSection = () => (
  <section className="py-12 px-6 md:px-12 bg-white/50">
    <div className="container-max">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-6">
          Certified & Accredited
        </p>
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
          <BWYBadge size="lg" />
          
          {/* Reiki Master Badge - No link as it's not BWY */}
          <div
            className="inline-flex flex-col items-center gap-2"
            title="Reiki Master Practitioner"
            data-testid="reiki-badge"
          >
            <div className="w-20 h-20 relative">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="50" cy="50" r="46" fill="none" stroke="#C5A059" strokeWidth="2" />
                {/* Reiki symbol simplified */}
                <path
                  d="M50 20 L50 45 M35 35 Q50 50 65 35 M40 55 Q50 70 60 55 M45 75 Q50 80 55 75"
                  fill="none"
                  stroke="#C5A059"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <span className="text-base text-neutral-600 text-center leading-tight">
              Reiki Master<br />Practitioner
            </span>
          </div>

          {/* Swedish Massage Therapist Badge */}
          <div
            className="inline-flex flex-col items-center gap-2"
            title="Swedish Massage Therapist"
            data-testid="massage-badge"
          >
            <div className="w-20 h-20 relative">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="50" cy="50" r="46" fill="none" stroke="#C5A059" strokeWidth="2" />
                {/* Two simplified hands / palms motif */}
                <path
                  d="M32 58 Q32 44 38 40 L38 30 Q38 26 42 26 Q46 26 46 30 L46 40 L48 40 L48 28 Q48 24 52 24 Q56 24 56 28 L56 40 L58 40 L58 32 Q58 28 62 28 Q66 28 66 32 L66 56 Q66 70 50 72 Q34 70 32 58 Z"
                  fill="none"
                  stroke="#C5A059"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="text-base text-neutral-600 text-center leading-tight">
              Swedish Massage<br />Therapist
            </span>
          </div>

          {/* Years of Experience Badge */}
          <div className="inline-flex flex-col items-center gap-2">
            <div className="w-20 h-20 relative flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="50" cy="50" r="46" fill="none" stroke="#C5A059" strokeWidth="2" />
                <text
                  x="50"
                  y="45"
                  textAnchor="middle"
                  className="fill-gold"
                  style={{ fontSize: '28px', fontWeight: '300' }}
                >
                  15+
                </text>
                <text
                  x="50"
                  y="62"
                  textAnchor="middle"
                  className="fill-neutral-600"
                  style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}
                >
                  YEARS
                </text>
              </svg>
            </div>
            <span className="text-base text-neutral-600 text-center leading-tight">
              Teaching<br />Experience
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// Compact inline badge for footer/about pages
export const BWYBadgeInline = ({ className = "" }) => (
  <a
    href="https://bwy.org.uk/"
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center gap-2 text-neutral-600 hover:text-gold transition-colors ${className}`}
    title="British Wheel of Yoga Certified Teacher"
    data-testid="bwy-badge-inline"
  >
    <div className="w-8 h-8">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="3" />
        <g stroke="currentColor" strokeWidth="2">
          <line x1="50" y1="10" x2="50" y2="90" />
          <line x1="10" y1="50" x2="90" y2="50" />
          <line x1="21.72" y1="21.72" x2="78.28" y2="78.28" />
          <line x1="78.28" y1="21.72" x2="21.72" y2="78.28" />
        </g>
        <circle cx="50" cy="50" r="12" fill="currentColor" />
      </svg>
    </div>
    <span className="text-sm font-medium">BWY Certified</span>
  </a>
);

export default BWYBadge;
